import { useState, useEffect, useContext, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Alert, { ALERT_TYPE } from '../../components/Alert';
import { PriceType, Wishes } from '../../models/types';
import { RepositoryContext } from '../../store/RepositoryProvider';
import Checkbox from '../../components/Checkbox';
import Table from '../../components/Table';
import { _times } from '../../utils/util';
import Section from '../../components/Section';
import MinusIcon from '../../components/icons/Minus';
import AddIcon from '../../components/icons/Add';
import Field from '../../components/Field';
import Input from '../../components/Input';
import Modal from '../../components/Modal'

const SelectPassengers = ({ prices = [], limits = {}, wishes = [], passengers = [], onApply, onCancel, passportRequired = false }) => {
  const {t} = useTranslation(['common', 'order']);

  const repository = useContext(RepositoryContext);

  const [newPassengers, setNewPassengers] = useState(passengers);
  const [newWishes, setNewWishes] = useState(wishes);
  const [newPassportsName, setNewPassportsName] = useState({});
  const [newPassportsNumber, setNewPassportsNumber] = useState({});
  const [newAges, setNewAges] = useState({});
  const [errors, setErrors] = useState([]);
  const [priceRows, setPriceRows] = useState([]);
  const [passportRows, setPassportRows] = useState([]);

    const [isOpenSelectAge, setIsOpenSelectAge] = useState(false);
    const [currentSelectKey, setCurrentSelectKey] = useState('');

    const handleIncrement = useCallback((excursionPrice, priceId) => {
        const passenger = newPassengers.find(p => p.excursion_price === excursionPrice.id);
        const currentCount = passenger ? passenger.count : 0;
        const newCount = currentCount + 1;

        handlePassengers(excursionPrice, priceId, newCount);

        const excPrice = prices.find(p => p.id === excursionPrice.id);
        if (excPrice && (excPrice.minAge != null || excPrice.maxAge != null)) {
            setCurrentSelectKey(`${excPrice.id}-${newCount - 1}`);
            setIsOpenSelectAge(true);
        }
    }, [newPassengers, prices]);

  const handleDecrement = useCallback((excursionPrice, priceId) => {
    const count = (newPassengers.find(passenger => passenger.excursion_price === excursionPrice.id) || {count: 0}).count - 1;
    handlePassengers(excursionPrice, priceId, count);
    delete newAges[`${excursionPrice.id}-${count}`];
    setNewAges({...newAges});
  }, [newPassengers, newAges]);


  const handleWishes = useCallback((wish, checked) => {
    let orderWishes = [...newWishes];
    let wishIdx = orderWishes.indexOf(wish);
    if (!checked) {
      if (wishIdx >= 0) {
        orderWishes.splice(wishIdx, 1);
      }
    } else {
      if (wishIdx === -1) {
        orderWishes.push(wish);
      }
    }

    setNewWishes([...orderWishes]);
  }, [newWishes]);


  const handlePassengers = useCallback((excursionPrice, priceId, count) => {
    let orderPassengers = [...newPassengers];
    let passengerIdx = orderPassengers.findIndex(passenger => passenger.excursion_price === excursionPrice.id);
    if (count <= 0) {
      if (passengerIdx >= 0) {
        orderPassengers.splice(passengerIdx, 1);
      }
    } else {
      if (passengerIdx === -1) {
        orderPassengers.push({ excursion_price: excursionPrice.id, price: priceId, count, passports: [], ages: [] });
      } else {
        orderPassengers[passengerIdx].count = Math.max(count, 0);
      }
    }

    setNewPassengers([...orderPassengers]);
  }, [newPassengers]);


    const buildPriceRows = useCallback(async () => {
        const rows = [];
        let idt = {};
        const idtInc = (type, count = 1) => {
            idt[type] = idt[type] || 0;
            idt[type] += count;
            return idt[type];
        };

        prices.sort((a, b) => a.price.id.localeCompare(b.price.id));

        for (let i = 0; i < prices.length; ++i) {
            const price = prices[i];
            let excPrice = prices.find((excPrice) => excPrice.id === price.id);
            let priceModel = (await repository.getModel('Price', price.price.id)).value;

            if (priceModel) {
                let info = priceModel.priceType === PriceType.CHILD
                    ? (priceModel.noSeat
                        ? t('order:price_type.no_seat')
                        : t('order:price_type.with_seat'))
                    : '';
                let title = priceModel.title;
                let desc = '';

                if (excPrice.minAge >= 0 || excPrice.maxAge >= 0) {
                    if (excPrice.minAge >= 0 && excPrice.maxAge >= 0) {
                        desc = ` ${t('order:select_passengers.age.fromto', {
                            from: excPrice.minAge,
                            to: excPrice.maxAge
                        })}`;
                    } else if (excPrice.minAge >= 0) {
                        desc = ` ${t('order:select_passengers.age.from', { age: excPrice.minAge })}`;
                    } else if (excPrice.maxAge >= 0) {
                        desc = ` ${t('order:select_passengers.age.to', { age: excPrice.maxAge })}`;
                    }
                }

                let passenger = newPassengers.find(
                    (passenger) => passenger.excursion_price === price.id
                );

                rows.push(
                    <Fragment key={`price_${i}`}>
                        <div className="price">
                            <div className="price-title" title={info}>
                                {title}
                                <div>
                                    <small>{desc}</small>
                                </div>
                                <div>
                                    <small>{excPrice.description}</small>
                                </div>
                            </div>
                            <span className="price-count flex-shrink-1">
                              <Button
                                  className="price-button"
                                  variant={BUTTON_TYPE.SECONDARY}
                                  onClick={() => handleDecrement(price, price.price.id)}
                              >
                                <MinusIcon />
                              </Button>
                            </span>
                            <span className="price-count_content">
                              {(passenger || { count: 0 }).count}
                            </span>
                            <span className="price-count flex-shrink-1">
                              <Button
                                  className="price-button"
                                  variant={BUTTON_TYPE.SECONDARY}
                                  onClick={() => handleIncrement(price, price.price.id)}
                              >
                                <AddIcon />
                              </Button>
                            </span>
                        </div>
                    </Fragment>
                );

                if (excPrice.minAge || excPrice.maxAge) {
                    let passports = [...Array(passenger ? passenger.count : 0)];

                    rows.push(
                        <div key={`age_${i}`} className="age-section">
                            {passports.map((e, j) => (
                                <div
                                    key={`age_${i}_${j}`}
                                    className={`age-title ${j % 2 ? 'last-child' : ''}`}
                                >
                                    <div>
                                        {t('order:select_passengers.child_age', {
                                            number: idtInc(priceModel.priceType)
                                        })}
                                    </div>
                                    <Button
                                        variant={BUTTON_TYPE.SECONDARY}
                                        onClick={() => {
                                            setCurrentSelectKey(`${excPrice.id}-${j}`);
                                            setIsOpenSelectAge(true);
                                        }}
                                    >
                                        {newAges[`${excPrice.id}-${j}`] >= 0
                                            ? t('order:select_passengers.age.age', {
                                                count: newAges[`${excPrice.id}-${j}`]
                                            })
                                            : t('order:select_passengers.select_age')}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    );
                } else {
                    idtInc(priceModel.priceType, passenger ? passenger.count : 0);
                }
            }
        }

        setPriceRows(rows);
    }, [newPassengers, newAges, repository, t]);



    const buildPassportRows = useCallback(async () => {
    const rows = [];

    let idt = {};
    const idtInc = (type, count = 1) => {
      idt[type] = idt[type] || 0;
      idt[type] += count;
      return idt[type];
    };

    for (let i = 0; i < prices.length; ++i) {
      const price = prices[i];
      let excPrice = prices.find((excPrice) => excPrice.id === price.id);
      let priceModel = (await repository.getModel('Price', price.price.id)).value;

      if (priceModel) {
        let passenger = newPassengers.find((passenger) => passenger.excursion_price === price.id);
        let passports = [...Array(passenger ? passenger.count : 0)];

        passports = passports.map((e, j) => (
          <Fragment key={`passport_${i}_${j}`}>
            <Field title={passportRequired ? (
                <>
                  {t(`order:select_passengers.passenger_${price.price.priceType}`, { number: idtInc(price.price.priceType) })}
                  <span className='field-note'>{price.price.priceType === PriceType.CHILD ? t('order:select_passengers.is_child') : t('order:select_passengers.is_adult')}</span>
                </>
              ) : ''}
            >
              <Table cols={1}>
                {passportRequired && (
                  <Table.Row>
                    <Table.Cell>
                      <Input
                        value={newPassportsName[`${excPrice.id}-${j}`]}
                        placeholder={t('order:select_passengers.passport.full_name')}
                        onChange={(value) => setNewPassportsName((prevState) => ({ ...prevState, [`${excPrice.id}-${j}`]: value }))}
                      />
                    </Table.Cell>
                  </Table.Row>
                )}

                {(passportRequired || excPrice.minAge || excPrice.maxAge) && (
                  <Table.Row className="gap-2">
                    {passportRequired && (
                      <Table.Cell colspan={(excPrice.minAge || excPrice.maxAge) ? 1 : 2}>
                        <Input
                          value={priceModel.priceType === PriceType.CHILD ? (newPassportsNumber[`${excPrice.id}-${j}`] ? newPassportsNumber[`${excPrice.id}-${j}`].padStart(11, '_') : '') : newPassportsNumber[`${excPrice.id}-${j}`]}
                          mask={priceModel.priceType === PriceType.CHILD ? [/[IVX ]/i, /[IVX ]/i, /[IVX ]/i, '-', /[А-Яа-я]/, /[А-Яа-я]/, ' ', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/] : '99 99 999999'}
                          placeholder={t(`order:select_passengers.passport.${priceModel.priceType === PriceType.CHILD ? 'birth_cert' : 'passport'}`)}
                          onChange={(value) => setNewPassportsNumber((prevState) => ({ ...prevState, [`${excPrice.id}-${j}`]: value.replace(/[_ -]/g,'') }))}
                        />
                      </Table.Cell>
                    )}
                  </Table.Row>
                )}
              </Table>
            </Field>
          </Fragment>
        ));

        rows.push(
          <Fragment key={`passport_${i}`}>
            {passports}
          </Fragment>
        );
      }
    }
    setPassportRows(rows);
  }, [newPassengers, newAges, newPassportsName, newPassportsNumber]);


  const validate = async () => {
    let newErrors = [...errors];

    let passengerCount = 0;
    let ageErrorCount = 0;

    for (let i = 0; i < newPassengers.length; ++i) {
      const price = (await repository.getModel('Price', newPassengers[i].price)).value;

      if (price && !price.noSeat) {
        passengerCount += newPassengers[i].count;
      }

      const excPrice = prices.find((excPrice) => excPrice.price.id === newPassengers[i].price && excPrice.id === newPassengers[i].excursion_price);
      const newAge = Object.keys(newAges).filter((a) => a.startsWith(newPassengers[i].excursion_price) && newAges[a] !== '-1');
      if (excPrice && (excPrice.minAge || excPrice.maxAge) && newPassengers[i].count !== newAge.length) {
        ageErrorCount++;
      }
    }

    let errorIdx = {
      zero_seat: newErrors.findIndex(error => error.code === 'zero_seat'),
      not_enough_seat: newErrors.findIndex(error => error.code === 'not_enough_seat'),
      child_age_empty: newErrors.findIndex(error => error.code === 'child_age_empty'),
    };

    if (ageErrorCount > 0) {
      if (errorIdx.child_age_empty === -1) {
        newErrors.push({ code: 'child_age_empty', type: ALERT_TYPE.ERROR, title: t('order:error.child_age_empty') });
      }
    } else {
      if (errorIdx.child_age_empty >= 0) {
        newErrors.splice(errorIdx.child_age_empty, 1);
      }
    }

    if (passengerCount === 0) {
      if (errorIdx.zero_seat === -1) {
        newErrors.push({ code: 'zero_seat', type: ALERT_TYPE.WARN, title: t('order:error.zero_seat') });
      }
    } else {
      if (errorIdx.zero_seat >= 0) {
        newErrors.splice(errorIdx.zero_seat, 1);
      }

      if (limits.stopSelling && passengerCount > limits.seats) {
        if (errorIdx.not_enough_seat === -1) {
          newErrors.push({ code: 'not_enough_seat', type: ALERT_TYPE.ERROR, title: t('order:error.not_enough_seat'), message: t('order:error.not_enough_seat_description') });
        }
      } else {
        if (errorIdx.not_enough_seat >= 0) {
          newErrors.splice(errorIdx.not_enough_seat, 1);
        }
      }
    }

    setErrors(newErrors);

    return newErrors.length === 0;
  };


  const handleReady = useCallback(async () => {
    let isValid = await validate();

    if (isValid) {
      Object.keys(newPassportsName).forEach((id) => {
        let [priceId, idx] = id.split('-');
        let passenger = newPassengers.find((passenger) => passenger.excursion_price === priceId);
        if (passenger) {
          passenger.passports = passenger.passports || [];
          passenger.passports[idx] = passenger.passports[idx] || {
            fullName: null,
            number: null
          };
          passenger.passports[idx].fullName = newPassportsName[id];
        }
      });
      Object.keys(newPassportsNumber).forEach((id) => {
        let [priceId, idx] = id.split('-');
        let passenger = newPassengers.find((passenger) => passenger.excursion_price === priceId);
        if (passenger) {
          passenger.passports = passenger.passports || [];
          passenger.passports[idx] = passenger.passports[idx] || {
            fullName: null,
            number: null
          };
          passenger.passports[idx].number = newPassportsNumber[id];
        }
      });
      Object.keys(newAges).forEach((id) => {
        let [priceId, idx] = id.split('-');
        let passenger = newPassengers.find((passenger) => passenger.excursion_price === priceId);
        if (passenger) {
          passenger.ages = passenger.ages ?? [];
          passenger.ages[idx] = newAges[id];
        }
      });

      onApply({
        passengers: newPassengers,
        wishes: newWishes
      });
    }
  }, [newPassengers, newWishes, newPassportsName, newPassportsNumber, newAges]);


  useEffect(() => {
    buildPriceRows();
    buildPassportRows();
  }, [newPassengers, newAges]);

  useEffect(() => {
    validate();
  }, [newPassengers, newAges]);

  useEffect(() => {
    let newPassportsName = {};
    let newPassportsNumber = {};
    let newAges = {};
    passengers.forEach((passenger) => {
      passenger.passports.forEach((passport, p) => {
        newPassportsName[`${passenger.excursion_price}-${p}`] = passport ? passport.fullName : '';
        newPassportsNumber[`${passenger.excursion_price}-${p}`] = passport ? passport.number : '';
      });
      passenger.ages.forEach((age, a) => {
        newAges[`${passenger.excursion_price}-${a}`] = age;
      });
    });

    setNewPassportsName(newPassportsName);
    setNewPassportsNumber(newPassportsNumber);
    setNewAges(newAges);
    setNewPassengers([ ...passengers ]);
  }, []);

  return (
    <div className="trip-app">
      <div className="trip-app-body">
        {errors.map((error, i) => (
          <Alert
              key={`error_${i}`} type={error.type || ALERT_TYPE.ERROR}
              title={error.title}
          >
              {error.message}
          </Alert>
        ))}

        <Section className="price-list static">
          {priceRows}
        </Section>

        <Section className="toggle-list static" title={t('order:select_passengers.wishes_for_seating')}>
          <Checkbox.Group>
            {Object.values(Wishes).map((wish) => (
              <Checkbox.Button key={wish} title={t(`order:wishes.${wish}`)} checked={newWishes.indexOf(wish) >= 0} value={wish} onChange={handleWishes} />
            ))}
          </Checkbox.Group>
        </Section>

        {passportRequired && (
          <Section className="passport-list static" title={t('order:select_passengers.passports')}>
            {passportRows}
          </Section>
        )}
      </div>
      <div className="trip-app-footer">
        <Button.Panel>
          <Button position={BUTTON_POSITION.LEFT} disabled={errors.length > 0} onClick={handleReady}>{t('common:button.next')}</Button>
          <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={onCancel}>{t('common:button.cancel')}</Button>
        </Button.Panel>
      </div>
        <Modal
            show={isOpenSelectAge}
            onClose={() => setIsOpenSelectAge(false)}
            className="age-selection-modal"
        >
            <Modal.Header>
                {t('order:select_passengers.select_age')}
            </Modal.Header>
            <Modal.Body>
                {(() => {
                    const [priceId] = currentSelectKey.split('-');
                    const excPrice = prices.find((p) => p.id === priceId);

                    if (!currentSelectKey) return null;

                    const minAge = excPrice.minAge || 0;
                    const maxAge = excPrice.maxAge || 14;

                    return (
                        <div className="age-options">
                            {_times((maxAge - minAge + 1), (i) => {
                                const age = minAge + i;
                                return (
                                    <Button
                                        key={age}
                                        variant={
                                            newAges[currentSelectKey] === age
                                                ? BUTTON_TYPE.PRIMARY
                                                : BUTTON_TYPE.SECONDARY
                                        }
                                        onClick={() => {
                                            setNewAges((prevState) => ({
                                                ...prevState,
                                                [currentSelectKey]: age
                                            }));
                                            setIsOpenSelectAge(false);
                                            setCurrentSelectKey('');
                                        }}
                                        className="age-option-button"
                                    >
                                        {t('order:select_passengers.age.age', { count: age })}
                                    </Button>
                                );
                            })}
                        </div>
                    );
                })()}
            </Modal.Body>
        </Modal>
    </div>
  );
}

SelectPassengers.propTypes = {
  prices: PropTypes.array,
  limits: PropTypes.object,
  passportRequired: PropTypes.bool,
  passengers: PropTypes.array,
  wishes: PropTypes.array,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
};

export default SelectPassengers;